/**
 * 主框架：頂部列（系統名、當前 tenant + role、切換產品、登出）+ 側邊導覽 + 內容區。
 * F0 階段側邊導覽連到佔位頁；F1 起逐一接上真實頁面。
 */

import { useMemo, useState } from 'react';
import { Layout, Menu, Dropdown, Button, Space, Tag, Typography, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  DatabaseOutlined,
  TeamOutlined,
  SettingOutlined,
  SwapOutlined,
  LogoutOutlined,
  UserOutlined,
  ApartmentOutlined,
  QuestionCircleOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSessionStore } from '../stores/sessionStore';
import { HelpDrawer } from '../features/help/HelpDrawer';

const { Header, Sider, Content } = Layout;

const ROLE_LABEL: Record<string, string> = {
  admin: '管理员',
  editor: '编辑',
  viewer: '审核员',
};

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentTenant, role, logout } = useSessionStore();
  const [helpOpen, setHelpOpen] = useState(false);

  const menuItems = useMemo(
    () => [
      { key: '/', icon: <AppstoreOutlined />, label: <Link to="/">概览</Link> },
      { key: '/boms', icon: <DatabaseOutlined />, label: <Link to="/boms">BOM 清单</Link> },
      { key: '/members', icon: <TeamOutlined />, label: <Link to="/members">成员/联系人</Link> },
      {
        key: '/contact-groups',
        icon: <TeamOutlined />,
        label: <Link to="/contact-groups">联系人分组</Link>,
      },
      ...(user?.is_platform_admin || role === 'admin'
        ? [
            {
              key: '/email-config',
              icon: <SettingOutlined />,
              label: <Link to="/email-config">发件配置</Link>,
            },
          ]
        : []),
      ...(user?.is_platform_admin || role === 'admin'
        ? [
            {
              key: '/audits',
              icon: <FileSearchOutlined />,
              label: <Link to="/audits">操作日志</Link>,
            },
          ]
        : []),
      ...(user?.is_platform_admin
        ? [
            {
              key: '/admin/users',
              icon: <SettingOutlined />,
              label: <Link to="/admin/users">用户管理</Link>,
            },
            {
              key: '/admin/tenants',
              icon: <ApartmentOutlined />,
              label: <Link to="/admin/tenants">产品管理</Link>,
            },
          ]
        : []),
    ],
    [user?.is_platform_admin, role],
  );

  const selectedKey =
    menuItems
      .map((m) => m.key)
      .filter((k) => k !== '/' && location.pathname.startsWith(k))
      .sort((a, b) => b.length - a.length)[0] ?? '/';

  const userMenu = {
    items: [
      { key: 'change-password', label: '修改密码' },
      { key: 'logout', icon: <LogoutOutlined />, label: '登出', danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'logout') {
        logout();
        navigate('/login');
      } else if (key === 'change-password') {
        navigate('/change-password');
      }
    },
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
          BOM 协作系统
        </Typography.Title>
        <Space size="middle">
          {currentTenant ? (
            <Space>
              <Tooltip title="点击切换产品">
                <Tag
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate('/select-tenant')}
                >
                  {currentTenant.name}
                </Tag>
              </Tooltip>
              {role && <Tag>{ROLE_LABEL[role] ?? role}</Tag>}
            </Space>
          ) : user?.is_platform_admin ? (
            <Tooltip title="点击选择产品">
              <Tag
                color="gold"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/select-tenant')}
              >
                平台管理员
              </Tag>
            </Tooltip>
          ) : null}
          <Button
            type="text"
            icon={<QuestionCircleOutlined />}
            style={{ color: '#fff' }}
            onClick={() => setHelpOpen(true)}
          >
            使用教程
          </Button>
          <Button
            type="text"
            icon={<SwapOutlined />}
            style={{ color: '#fff' }}
            onClick={() => navigate('/select-tenant')}
          >
            切换产品
          </Button>
          <Dropdown menu={userMenu}>
            <Button type="text" icon={<UserOutlined />} style={{ color: '#fff' }}>
              {user?.name ?? user?.email}
            </Button>
          </Dropdown>
        </Space>
      </Header>
      <Layout>
        <Sider width={220} theme="light">
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            items={menuItems}
            style={{ height: '100%' }}
          />
        </Sider>
        <Content style={{ padding: 24 }}>
          <Outlet />
        </Content>
      </Layout>
      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Layout>
  );
}
